import { Component, OnInit, OnDestroy } from '@angular/core';
import { Chart, registerables } from 'chart.js';

// This for chart.s compnetnss
Chart.register(...registerables);

interface RecentComplaint {
	id: string;
	category: string;
	customer: string;
	priority: 'High' | 'Medium' | 'Low';
	slaStatus: 'Out of SLA' | 'Approaching' | 'Within SLA';
	daysOpen: number;
}

@Component({
	selector: 'app-dashboard-total',
	standalone: false,
	templateUrl: './dashboard-total.component.html',
	styleUrls: ['./dashboard-total.component.scss'],
})
export class DashboardTotalComponent implements OnInit, OnDestroy {
	totalComplaints = 1248;
	withinSla = 892;
	approachingSla = 187;
	outOfSla = 169;

	// Recent Complaints Mock Dta
	recentComplaints: RecentComplaint[] = [
		{
			id: '#COMP-4821',
			category: 'Billing Issue',
			customer: 'John Smith',
			priority: 'High',
			slaStatus: 'Out of SLA',
			daysOpen: 12,
		},
		{
			id: '#COMP-4756',
			category: 'Technical Support',
			customer: 'Sarah Johnson',
			priority: 'Medium',
			slaStatus: 'Approaching',
			daysOpen: 8,
		},
		{
			id: '#COMP-4892',
			category: 'Product Quality',
			customer: 'Michael Brown',
			priority: 'High',
			slaStatus: 'Out of SLA',
			daysOpen: 15,
		},
		{
			id: '#COMP-4911',
			category: 'Service Delivery',
			customer: 'Emily Davis',
			priority: 'Medium',
			slaStatus: 'Within SLA',
			daysOpen: 3,
		},
		{
			id: '#COMP-4873',
			category: 'Technical Support',
			customer: 'Robert Wilson',
			priority: 'Low',
			slaStatus: 'Approaching',
			daysOpen: 9,
		},
	];

	private slaPieChart?: Chart;
	private resolutionTimeChart?: Chart;
	private complaintsTrendChart?: Chart;

	ngOnInit(): void {
		setTimeout(() => {
			this.createSlaPieChart();
			this.createResolutionTimeChart();
			this.createComplaintsTrendChart();
		});
	}

	ngOnDestroy(): void {
		// To Avoid Memory leaks
		this.slaPieChart?.destroy();
		this.resolutionTimeChart?.destroy();
		this.complaintsTrendChart?.destroy();
	}

	// SLA Doughnut chart
	private createSlaPieChart(): void {
		const canvas = document.getElementById(
			'slaPieChart'
		) as HTMLCanvasElement | null;
		if (!canvas) {
			return;
		}

		const total = this.withinSla + this.approachingSla + this.outOfSla;

		this.slaPieChart = new Chart(canvas.getContext('2d')!, {
			type: 'doughnut',
			data: {
				labels: ['Within SLA', 'Approaching SLA', 'Out of SLA'],
				datasets: [
					{
						data: [
							this.withinSla,
							this.approachingSla,
							this.outOfSla,
						],
						backgroundColor: ['#047e20', '#e8ae01', '#eb031a'],
						borderWidth: 0,
					},
				],
			},
			options: {
				responsive: true,
				maintainAspectRatio: false,
				cutout: '70%',
				plugins: {
					legend: { display: false },
					tooltip: {
						callbacks: {
							label: (context) => {
								const label = context.label ?? '';
								const value = Number(context.raw ?? 0);
								const total =
									this.withinSla +
									this.approachingSla +
									this.outOfSla;
								const percentage = Math.round(
									(value / total) * 100
								);
								return `${label}: ${value} (${percentage}%)`;
							},
						},
					},
				},
			},
		});
	}

	// Resolution Timeline
	private createResolutionTimeChart(): void {
		const canvas = document.getElementById(
			'resolutionTimeChart'
		) as HTMLCanvasElement | null;
		if (!canvas) {
			return;
		}

		this.resolutionTimeChart = new Chart(canvas.getContext('2d')!, {
			type: 'line',
			data: {
				labels: [
					'Jan',
					'Feb',
					'Mar',
					'Apr',
					'May',
					'Jun',
					'Jul',
					'Aug',
					'Sep',
					'Oct',
					'Nov',
					'Dec',
				],
				datasets: [
					{
						label: 'Average Resolution Time (Days)',
						data: [
							5.2, 4.8, 5.5, 6.1, 5.9, 5.3, 4.9, 4.7, 5.1, 4.8,
							4.5, 4.3,
						],
						borderColor: '#0f1e3b',
						backgroundColor: 'rgba(15, 30, 59, 0.1)',
						borderWidth: 2,
						fill: true,
						tension: 0.4,
						pointBackgroundColor: '#0f1e3b',
						pointRadius: 4,
						pointHoverRadius: 6,
					},
				],
			},
			options: {
				responsive: true,
				maintainAspectRatio: false,
				scales: {
					y: {
						beginAtZero: false,
						min: 4,
						title: { display: true, text: 'Days' },
					},
				},
				plugins: {
					legend: { display: false },
					tooltip: {
						callbacks: {
							label: (context) =>
								`Avg. Resolution: ${context.raw} days`,
						},
					},
				},
			},
		});
	}

	// Complaints trends
	private createComplaintsTrendChart(): void {
		const canvas = document.getElementById(
			'complaintsTrendChart'
		) as HTMLCanvasElement | null;
		if (!canvas) {
			return;
		}

		this.complaintsTrendChart = new Chart(canvas.getContext('2d')!, {
			type: 'line',
			data: {
				labels: [
					'Jan',
					'Feb',
					'Mar',
					'Apr',
					'May',
					'Jun',
					'Jul',
					'Aug',
					'Sep',
					'Oct',
					'Nov',
					'Dec',
				],
				datasets: [
					{
						label: 'Monthly Complaints',
						data: [
							95, 112, 108, 124, 98, 87, 76, 92, 105, 118, 132,
							101,
						],
						borderColor: '#fa6814',
						backgroundColor: 'rgba(250, 104, 20, 0.1)',
						borderWidth: 2,
						fill: true,
						tension: 0.4,
						pointBackgroundColor: '#fa6814',
						pointRadius: 4,
						pointHoverRadius: 6,
					},
				],
			},
			options: {
				responsive: true,
				maintainAspectRatio: false,
				scales: {
					y: {
						beginAtZero: true,
						title: { display: true, text: 'Number of Complaints' },
					},
				},
				plugins: {
					legend: { display: false },
					tooltip: {
						callbacks: {
							label: (context) => `Complaints: ${context.raw}`,
						},
					},
				},
			},
		});
	}
}
